// Client-side image compression + data-URL encoding for admin product uploads.
// Why client-side: avoids needing a backend file-storage endpoint, works with all
// image formats the browser can decode (JPEG, PNG, WEBP, HEIC on iOS, GIF, BMP),
// and the resulting data URL plugs directly into an existing string-based image field.

const DEFAULT_MAX_DIMENSION = 1200; // px on the longest side
const DEFAULT_QUALITY = 0.85;
const MAX_INPUT_SIZE_MB = 15; // raw file size cap before compression

/**
 * Compresses an image File to a JPEG data URL.
 * @param {File} file - The image file from <input type="file">
 * @param {object} opts
 * @param {number} [opts.maxDimension=1200] - Max width/height in pixels.
 * @param {number} [opts.quality=0.85] - JPEG quality 0-1.
 * @returns {Promise<string>} - Data URL like "data:image/jpeg;base64,..."
 */
export const compressImageToDataURL = (file, opts = {}) =>
  new Promise((resolve, reject) => {
    const maxDim = opts.maxDimension || DEFAULT_MAX_DIMENSION;
    const quality = opts.quality || DEFAULT_QUALITY;

    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      reject(new Error('File must be an image'));
      return;
    }

    if (file.size > MAX_INPUT_SIZE_MB * 1024 * 1024) {
      reject(new Error(`Image must be under ${MAX_INPUT_SIZE_MB} MB`));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.onload = () => {
        let { width, height } = img;

        // Scale down so the longest side <= maxDim, preserve aspect ratio
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; // white background for transparent PNGs
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Export as JPEG to maximize compression
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export default compressImageToDataURL;
